import { useClient } from '@cortex/react-query';
import {
  AgentChat,
  AgentImageSourceProvider,
  ElicitationScopeProvider,
  ToolRenderProvider,
  type IAgentImageSource,
} from '@formant/chat';
import { useMemo, type ComponentProps } from 'react';

import { consoleToolRenderSeams } from '../chat/host-seams';

/**
 * The package `AgentChat` pre-wired with the console's tool-render seams
 * (memory cards + fleet viz). Every console surface that embeds a transcript
 * directly (the /agents thread page, the orchestration-graph sidebar) must
 * render THIS, not the bare package component — a bare `AgentChat` silently
 * falls back to default tool cards and the regression is invisible to
 * typecheck/lint/tests.
 *
 * `persistentAgentId` (the thread's PA row id) additionally wires the
 * spilled-image seam: tool results carry tiny `image_refs` metas whose bytes
 * live behind `GET /v1/persistent-agents/:id/images/:ref`; with the id we bind
 * the console's bearer-authed client so the transcript renders them. Omitted →
 * refs render nothing (unchanged behavior).
 */
export function ConsoleAgentChat({
  persistentAgentId,
  groupId,
  ...props
}: ComponentProps<typeof AgentChat> & { persistentAgentId?: string; groupId?: string }) {
  const client = useClient();
  const imageSource = useMemo<IAgentImageSource | null>(
    () =>
      persistentAgentId
        ? {
            persistentAgentId,
            fetchImage: async (ref: string) => {
              try {
                return await client.http
                  .get(`v1/persistent-agents/${persistentAgentId}/images/${ref}`)
                  .blob();
              } catch {
                // 404 (image swept / never uploaded / archive unconfigured) or
                // any transport failure — the gallery silently omits it.
                return null;
              }
            },
          }
        : null,
    [client, persistentAgentId],
  );
  // Orchestration / thread pages render AgentChat without PaChatView, so the
  // inline decision card never saw {groupId, threadId} unless we provide it.
  // Do not fall back to ChatProvider's formant-chat agent id — that's a
  // different agent and would answer elicitations on the wrong thread.
  const elicitationScope = useMemo(
    () => (groupId && persistentAgentId ? { groupId, threadId: persistentAgentId } : null),
    [groupId, persistentAgentId],
  );
  const chat = <AgentChat {...props} />;
  const withImages = imageSource ? (
    <AgentImageSourceProvider value={imageSource}>{chat}</AgentImageSourceProvider>
  ) : (
    chat
  );
  const withElicitation = elicitationScope ? (
    <ElicitationScopeProvider value={elicitationScope}>{withImages}</ElicitationScopeProvider>
  ) : (
    withImages
  );
  return <ToolRenderProvider value={consoleToolRenderSeams}>{withElicitation}</ToolRenderProvider>;
}
